import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ExternalLink, FileText, Search, Trash2, Upload } from "lucide-react";
import {
  api,
  currentPeriod,
  euro,
  formatBytes,
  formatDate,
  periodLabel,
  type ChecklistEntry,
  type DocumentRow,
  type MessageDetail,
  type VatSummary,
} from "../api";
import { VendorsPage } from "./VendorsPage";
import { AKTION_LABEL, BelegRegelnTab } from "./BelegRegeln";
import { Select } from "./Select";
import { MailRahmen } from "./MailRahmen";
import { bestaetigen, datumDe, fehlerText, toast, useDialog, zahlFeld, zahlLesen } from "./verwaltung";

type Tab = "ustva" | "dokumente" | "quellen" | "regeln";

/** Obergrenze des Servers für eine Datei (MAX_UPLOAD). */
const MAX_DATEI = 25 * 1024 * 1024;

const STATE_LABEL: Record<ChecklistEntry["state"], string> = {
  da: "liegt vor",
  fehlt: "steht noch aus",
  ueberfaellig: "überfällig",
  nicht_erwartet: "in diesem Monat nicht erwartet",
};

const SOURCE_LABEL: Record<string, string> = {
  email_attachment: "Mail-Anhang",
  email_link: "Link in Mail",
  portal: "Portal",
  api: "Anbieter-API",
  manuell: "von Hand",
};

function periodeVersetzen(period: string, delta: number): string {
  const [y, m] = period.split("-").map(Number);
  const d = new Date(y ?? 2000, (m ?? 1) - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function BelegePage() {
  const [tab, setTab] = useState<Tab>("ustva");
  const [period, setPeriod] = useState(currentPeriod());
  const [periods, setPeriods] = useState<{ period: string; n: number; gross: number; problems: number }[]>([]);

  const ladePerioden = useCallback(() => { api.periods().then(setPeriods).catch(() => {}); }, []);
  useEffect(ladePerioden, [ladePerioden]);

  // Die letzten 24 Monate sind immer wählbar — auch ein Monat ganz ohne Beleg
  // muss sich öffnen lassen, gerade dann fehlt ja etwas.
  const options = useMemo(() => {
    const set = new Set<string>();
    for (let i = 0; i < 24; i++) set.add(periodeVersetzen(currentPeriod(), -i));
    for (const p of periods) set.add(p.period);
    set.add(period);
    const info = new Map(periods.map((p) => [p.period, p]));
    return [...set].sort().reverse().map((p) => {
      const i = info.get(p);
      return {
        value: p,
        label: periodLabel(p),
        hint: i ? `${i.n} ${i.n === 1 ? "Beleg" : "Belege"}${i.problems ? ` · ${i.problems} prüfen` : ""}` : undefined,
      };
    });
  }, [periods, period]);

  const istAktuell = period >= currentPeriod();

  return (
    <section className="pane wide" data-active="true">
      <header className="pane-head">
        <span className="pane-title">Belege</span>
        <nav className="tabs" aria-label="Bereiche">
          {([["ustva", "Voranmeldung"], ["dokumente", "Dokumente"], ["quellen", "Quellen"], ["regeln", "Regeln"]] as const).map(([id, label]) => (
            <button key={id} className="tab" aria-current={tab === id} onClick={() => setTab(id)}>
              {label}
            </button>
          ))}
        </nav>
        <div className="spacer" />
        {tab !== "quellen" && tab !== "regeln" && (
          <div className="v-monat" role="group" aria-label="Monat wählen">
            <button className="icon-btn" onClick={() => setPeriod(periodeVersetzen(period, -1))} aria-label="Vorheriger Monat" title="Vorheriger Monat">
              <ChevronLeft aria-hidden="true" />
            </button>
            <Select variant="button" value={period} onChange={setPeriod} options={options} aria-label="Monat" searchable={false} />
            <button className="icon-btn" onClick={() => setPeriod(periodeVersetzen(period, 1))} disabled={istAktuell}
              aria-label="Nächster Monat" title="Nächster Monat">
              <ChevronRight aria-hidden="true" />
            </button>
          </div>
        )}
      </header>

      <div className="pane-scroll">
        {tab === "ustva" && <UstvaTab period={period} onZuDokumenten={() => setTab("dokumente")} />}
        {tab === "dokumente" && <DocumentsTab period={period} onGeaendert={ladePerioden} />}
        {tab === "quellen" && <VendorsPage />}
        {tab === "regeln" && <BelegRegelnTab onGeaendert={ladePerioden} />}
      </div>
    </section>
  );
}

// ─── Voranmeldung ────────────────────────────────────────────────────────────

function UstvaTab({ period, onZuDokumenten }: { period: string; onZuDokumenten: () => void }) {
  const [data, setData] = useState<VatSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [versuch, setVersuch] = useState(0);

  useEffect(() => {
    let aktiv = true;
    setData(null);
    setError(null);
    api.ustva(period)
      .then((d) => { if (aktiv) setData(d); })
      .catch((e) => { if (aktiv) setError(fehlerText(e)); });
    return () => { aktiv = false; };
  }, [period, versuch]);

  if (error) {
    return (
      <div className="page">
        <div className="v-leer" data-art="fehler">
          <strong>Die Voranmeldung ließ sich nicht berechnen</strong>
          <span>{error}</span>
          <button className="btn" onClick={() => setVersuch((v) => v + 1)}>Erneut versuchen</button>
        </div>
      </div>
    );
  }
  if (!data) return <div className="v-laden" role="status">Rechne {periodLabel(period)} zusammen …</div>;

  const open = data.missing.length;

  return (
    <div className="page">
      <div className="v-kopf">
        <div>
          <h2>Umsatzsteuervoranmeldung {periodLabel(period)}</h2>
          <p className="lede">
            Zusammengerechnet aus {data.totals.documents} {data.totals.documents === 1 ? "Beleg, der" : "Belegen, die"} in
            die Voranmeldung {data.totals.documents === 1 ? "zählt" : "zählen"}. Das ist eine Vorbereitung, keine
            Steuerberatung — bitte vor der Abgabe prüfen.
          </p>
        </div>
      </div>

      <div className="stat-grid">
        <div className="stat">
          <div className="n">{euro(data.totals.vorsteuerGesamt)}</div>
          <div className="k">Vorsteuer gesamt</div>
        </div>
        <div className="stat">
          <div className="n">{euro(data.totals.gross)}</div>
          <div className="k">Eingangsbelege brutto</div>
        </div>
        <div className="stat">
          <div className="n">{data.totals.documents}</div>
          <div className="k">Belege, die zählen</div>
        </div>
        <div className="stat" data-warn={open > 0}>
          <div className="n">{open}</div>
          <div className="k">{open === 1 ? "erwarteter Beleg fehlt" : "erwartete Belege fehlen"}</div>
        </div>
      </div>

      {open > 0 && (
        <section className="v-abschnitt">
          <h3>Das fehlt noch</h3>
          <p className="v-abschnitt-lede">
            Solange hier etwas steht, ist die Voranmeldung unvollständig. Bei Portal-Quellen steht dabei,
            woran der letzte Abholversuch gescheitert ist.
          </p>
          {data.missing.map((entry) => (
            <div className="rule" key={entry.vendorId}>
              <div className="rule-head" style={{ cursor: "default" }}>
                <span className="state-dot" data-state={entry.state} aria-hidden="true" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="rule-name">{entry.vendor}</div>
                  <div className="rule-summary">
                    {STATE_LABEL[entry.state]}
                    {entry.expectedDay ? `, erwartet um den ${entry.expectedDay}.` : ""}
                    {entry.lastSeenPeriod ? ` — zuletzt ${periodLabel(entry.lastSeenPeriod)}` : " — noch nie eingegangen"}
                  </div>
                  {entry.lastRun && entry.lastRun.status === "fehler" && (
                    <div className="rule-summary" style={{ color: "var(--warn)" }}>
                      Letzter Abholversuch gescheitert: {firstProblem(entry.lastRun.detail)}
                    </div>
                  )}
                </div>
                <span className="chip">{SOURCE_LABEL[entry.sourceType] ?? entry.sourceType}</span>
              </div>
            </div>
          ))}
        </section>
      )}

      <section className="v-abschnitt">
        <h3>Vorsteuer nach Steuersatz</h3>
        <p className="v-abschnitt-lede">Deutsche Eingangsrechnungen mit ausgewiesener Umsatzsteuer — Kennziffer 66.</p>
        <div className="v-tabelle">
          <table className="log">
            <thead>
              <tr>
                <th>Steuersatz</th>
                <th className="v-num">Belege</th>
                <th className="v-num">Netto</th>
                <th className="v-num">Vorsteuer</th>
              </tr>
            </thead>
            <tbody>
              {data.vorsteuer.length === 0 ? (
                <tr><td colSpan={4} className="v-zelle-leer">Keine regulär besteuerten Eingangsrechnungen in diesem Monat.</td></tr>
              ) : (
                data.vorsteuer.map((r) => (
                  <tr key={r.rate}>
                    <td>{r.rate.toLocaleString("de-DE")} %</td>
                    <td className="v-num">{r.count}</td>
                    <td className="v-num">{euro(r.net)}</td>
                    <td className="v-num">{euro(r.vat)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {(data.reverseCharge.count > 0 || data.igErwerb.count > 0 || data.steuerfrei.count > 0) && (
        <section className="v-abschnitt">
          <h3>Sonderfälle</h3>
          <p className="v-abschnitt-lede">
            Leistungen aus dem EU-Ausland (typisch Adobe, Google, Meta) versteuerst du selbst und ziehst die
            Steuer im selben Zug als Vorsteuer ab. Sie gehören in andere Felder als die regulären Rechnungen.
          </p>
          <div className="v-tabelle">
            <table className="log">
              <thead>
                <tr>
                  <th>Art</th>
                  <th className="v-num">Belege</th>
                  <th className="v-num">Bemessungsgrundlage</th>
                  <th className="v-num">Steuer</th>
                  <th>Kennziffer</th>
                </tr>
              </thead>
              <tbody>
                {data.reverseCharge.count > 0 && (
                  <tr>
                    <td>Leistungen nach §13b (Reverse-Charge)</td>
                    <td className="v-num">{data.reverseCharge.count}</td>
                    <td className="v-num">{euro(data.reverseCharge.net)}</td>
                    <td className="v-num">{euro(data.reverseCharge.vat)}</td>
                    <td>46 / 47, Vorsteuer 67</td>
                  </tr>
                )}
                {data.igErwerb.count > 0 && (
                  <tr>
                    <td>Innergemeinschaftlicher Erwerb</td>
                    <td className="v-num">{data.igErwerb.count}</td>
                    <td className="v-num">{euro(data.igErwerb.net)}</td>
                    <td className="v-num">{euro(data.igErwerb.vat)}</td>
                    <td>89 / 93, Vorsteuer 61</td>
                  </tr>
                )}
                {data.steuerfrei.count > 0 && (
                  <tr>
                    <td>Ohne Umsatzsteuer</td>
                    <td className="v-num">{data.steuerfrei.count}</td>
                    <td className="v-num">{euro(data.steuerfrei.net)}</td>
                    <td className="v-num">—</td>
                    <td>keine</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {data.problems.length > 0 && (
        <section className="v-abschnitt">
          <div className="v-abschnitt-kopf">
            <div>
              <h3>Belege, die du ansehen solltest</h3>
              <p className="v-abschnitt-lede">Hier ist die automatische Auslesung unsicher.</p>
            </div>
            <button className="btn" onClick={onZuDokumenten}>Zu den Dokumenten</button>
          </div>
          <div className="v-tabelle">
            <table className="log">
              <thead>
                <tr><th>Anbieter</th><th>Befund</th></tr>
              </thead>
              <tbody>
                {data.problems.map((p) => (
                  <tr key={p.id}><td>{p.vendor || "—"}</td><td>{p.issue}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

function firstProblem(detail: string): string {
  try {
    const parsed = JSON.parse(detail) as { fehler?: string | string[] };
    const f = parsed.fehler;
    return Array.isArray(f) ? f[0] ?? "unbekannt" : f ?? "unbekannt";
  } catch {
    return "unbekannt";
  }
}

// ─── Dokumente ───────────────────────────────────────────────────────────────

type Status = "neu" | "geprueft" | "problem" | "dublette" | "ausgang" | "kein_beleg" | "unterlage" | "fremd" | "ignoriert";

const STATUS: Record<Status, { label: string; zaehlt: boolean; hint: string }> = {
  neu: { label: "Neu", zaehlt: true, hint: "Automatisch erkannt, noch nicht von dir geprüft — zählt trotzdem schon mit." },
  geprueft: { label: "Geprüft", zaehlt: true, hint: "Von dir bestätigt." },
  problem: { label: "Prüfen", zaehlt: false, hint: "Auslesung unsicher — zählt erst nach deiner Prüfung." },
  dublette: { label: "Doppelt", zaehlt: false, hint: "Dieselbe Rechnung liegt schon vor." },
  ausgang: { label: "Ausgangsrechnung", zaehlt: false, hint: "Eigene Rechnung — keine Vorsteuer." },
  kein_beleg: { label: "Kein Beleg", zaehlt: false, hint: "Keine Rechnung, etwa AGB oder Preisliste." },
  unterlage: { label: "Unterlage", zaehlt: false, hint: "Auswertung, Lohn, Bescheid — abgelegt, aber kein Beleg." },
  fremd: { label: "Nicht an dich", zaehlt: false, hint: "Rechnung an jemand anderen, etwa die Bestellung eines Kunden." },
  ignoriert: { label: "Aussortiert", zaehlt: false, hint: "Per Beleg-Regel aussortiert." },
};
const STATUS_REIHE = Object.keys(STATUS) as Status[];
const statusInfo = (s: string) => STATUS[s as Status] ?? { label: s, zaehlt: false, hint: "" };

const TAX_KURZ: Record<string, string> = {
  regulaer: "USt",
  reverse_charge: "§13b",
  ig_erwerb: "ig. Erwerb",
  steuerfrei: "ohne USt",
  kein_beleg: "kein Beleg",
};

type Filter = "relevant" | "alle" | "zaehlt" | "zaehlt_nicht" | Status;

/** Was in die Voranmeldung gehört oder noch entschieden werden muss — der Normalfall. */
const istRelevant = (status: string) => statusInfo(status).zaehlt || status === "problem";

function DocumentsTab({ period, onGeaendert }: { period: string; onGeaendert: () => void }) {
  const [docs, setDocs] = useState<DocumentRow[] | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [editing, setEditing] = useState<DocumentRow | null>(null);
  const [upload, setUpload] = useState<{ fertig: number; gesamt: number } | null>(null);
  const [filter, setFilter] = useState<Filter>("relevant");
  const [suche, setSuche] = useState("");
  const [zieht, setZieht] = useState(false);
  const eingabe = useRef<HTMLInputElement | null>(null);

  const load = useCallback(async () => {
    try {
      setDocs(await api.documents({ period }));
      setLadeFehler(null);
    } catch (e) {
      setLadeFehler(fehlerText(e));
    }
  }, [period]);

  useEffect(() => { setDocs(null); setFilter("relevant"); void load(); }, [load]);

  const hochladen = async (files: File[]) => {
    const pdfs = files.filter((f) => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf"));
    if (pdfs.length === 0) { toast.fehler("Nur PDF-Dateien lassen sich als Beleg hochladen."); return; }
    const zuGross = pdfs.filter((f) => f.size > MAX_DATEI);
    const ok = pdfs.filter((f) => f.size <= MAX_DATEI);
    setUpload({ fertig: 0, gesamt: ok.length });
    let neu = 0, doppelt = 0;
    const fehler: string[] = zuGross.map((f) => `${f.name}: größer als ${formatBytes(MAX_DATEI)}`);
    for (const [i, file] of ok.entries()) {
      try {
        const result = await api.uploadDocument(file.name, await alsBase64(file));
        if (result.duplicate) doppelt++; else neu++;
      } catch (err) {
        fehler.push(`${file.name}: ${fehlerText(err)}`);
      }
      setUpload({ fertig: i + 1, gesamt: ok.length });
    }
    setUpload(null);
    await load();
    onGeaendert();
    const teile = [
      neu ? `${neu} ${neu === 1 ? "Beleg" : "Belege"} abgelegt` : "",
      doppelt ? `${doppelt} schon vorhanden (gleiche Datei)` : "",
    ].filter(Boolean);
    if (teile.length) toast.erfolg(`${teile.join(" · ")}. Neue Belege landen im Monat ihres Rechnungsdatums.`, { dauer: 6000 });
    if (fehler.length) toast.fehler(`Nicht hochgeladen: ${fehler.join("; ")}`, { dauer: 0 });
  };

  const liste = docs ?? [];
  const summen = useMemo(() => {
    const s = new Map<Filter, { n: number; brutto: number }>();
    const add = (k: Filter, d: DocumentRow) => {
      const v = s.get(k) ?? { n: 0, brutto: 0 };
      v.n++; v.brutto += d.gross ?? 0;
      s.set(k, v);
    };
    for (const d of liste) {
      add("alle", d);
      if (istRelevant(d.status)) add("relevant", d);
      add(d.status as Status, d);
      add(statusInfo(d.status).zaehlt ? "zaehlt" : "zaehlt_nicht", d);
    }
    return s;
  }, [liste]);

  const sichtbar = useMemo(() => {
    const q = suche.trim().toLowerCase();
    return liste.filter((d) => {
      const z = statusInfo(d.status).zaehlt;
      if (filter === "relevant" && !istRelevant(d.status)) return false;
      if (filter === "zaehlt" && !z) return false;
      if (filter === "zaehlt_nicht" && z) return false;
      if (!["relevant", "alle", "zaehlt", "zaehlt_nicht"].includes(filter) && d.status !== filter) return false;
      if (!q) return true;
      return [d.vendor_name, d.invoice_number, d.file_name, d.mail_subject].some((t) => t?.toLowerCase().includes(q));
    });
  }, [liste, filter, suche]);

  const kachel = (id: Filter, label: string, titel?: string) => {
    const s = summen.get(id) ?? { n: 0, brutto: 0 };
    return (
      <button key={id} className="v-filter-knopf" aria-pressed={filter === id} onClick={() => setFilter(id)} title={titel}
        data-art={id === "zaehlt" ? "ok" : id === "problem" ? "warn" : undefined}>
        <span className="v-filter-label">{label}</span>
        <span className="v-filter-n">{s.n}</span>
        <span className="v-filter-summe">{euro(s.brutto)}</span>
      </button>
    );
  };

  return (
    <div className="page"
      onDragOver={(e) => { if (e.dataTransfer.types.includes("Files")) { e.preventDefault(); setZieht(true); } }}
      onDragLeave={(e) => { if (e.currentTarget === e.target) setZieht(false); }}
      onDrop={(e) => { e.preventDefault(); setZieht(false); if (!upload) void hochladen([...e.dataTransfer.files]); }}
      data-zieht={zieht}
    >
      <div className="v-kopf">
        <div>
          <h2>Dokumente {periodLabel(period)}</h2>
          <p className="lede">
            Abgelegt unter <code className="mono">belege/{period.slice(0, 4)}/{period}/</code>. Doppelte Dateien erkennt
            der Agent automatisch, egal aus welcher Quelle sie kommen.
          </p>
        </div>
        <div className="v-kopf-aktionen">
          <button className="btn primary" onClick={() => eingabe.current?.click()} disabled={upload !== null}>
            <Upload aria-hidden="true" />
            {upload ? `Lade hoch … ${upload.fertig}/${upload.gesamt}` : "PDF hochladen"}
          </button>
          <input
            ref={eingabe}
            type="file"
            accept="application/pdf,.pdf"
            multiple
            hidden
            onChange={(e) => {
              const files = [...(e.target.files ?? [])];
              e.target.value = "";
              if (files.length) void hochladen(files);
            }}
          />
        </div>
      </div>

      {ladeFehler && !docs ? (
        <div className="v-leer" data-art="fehler">
          <strong>Die Belege ließen sich nicht laden</strong>
          <span>{ladeFehler}</span>
          <button className="btn" onClick={() => void load()}>Erneut versuchen</button>
        </div>
      ) : !docs ? (
        <div className="v-laden" role="status">Lade Belege …</div>
      ) : docs.length === 0 ? (
        <div className="v-leer">
          <FileText aria-hidden="true" />
          <strong>Noch keine Belege in {periodLabel(period)}</strong>
          <span>Der Agent legt Rechnungen ab, sobald sie im Postfach ankommen. Du kannst PDFs auch hierher ziehen.</span>
        </div>
      ) : (
        <>
          <div className="v-filter" role="group" aria-label="Nach Status filtern">
            {kachel("relevant", "Belege", "Zählt oder muss noch geprüft werden — ohne Unterlagen, fremde Rechnungen und Aussortiertes")}
            {kachel("alle", "Alle")}
            {kachel("zaehlt", "Zählt", "Neu und geprüft — fließt in die Voranmeldung ein")}
            {kachel("zaehlt_nicht", "Zählt nicht", "Prüfen, doppelt, Ausgangsrechnung, Unterlage, nicht an dich, kein Beleg")}
            <span className="v-filter-trenner" aria-hidden="true" />
            {STATUS_REIHE.filter((s) => (summen.get(s)?.n ?? 0) > 0).map((s) => kachel(s, STATUS[s].label, STATUS[s].hint))}
          </div>

          <div className="v-werkzeuge">
            <label className="v-suche">
              <Search aria-hidden="true" />
              <input type="search" value={suche} onChange={(e) => setSuche(e.target.value)}
                placeholder="Anbieter, Nummer oder Betreff" aria-label="Belege durchsuchen" />
            </label>
            <span className="hint" style={{ margin: 0 }}>
              {sichtbar.length} von {liste.length} · Summe brutto {euro(sichtbar.reduce((s, d) => s + (d.gross ?? 0), 0))}
            </span>
          </div>

          <div className="v-tabelle">
            <table className="log v-belege">
              <thead>
                <tr>
                  <th>Anbieter</th>
                  <th>Datum</th>
                  <th className="v-schmal-weg">Nummer</th>
                  <th className="v-num">Brutto</th>
                  <th className="v-num v-schmal-weg">Vorsteuer</th>
                  <th>Status</th>
                  <th><span className="v-sr">Aktion</span></th>
                </tr>
              </thead>
              <tbody>
                {sichtbar.length === 0 ? (
                  <tr><td colSpan={7} className="v-zelle-leer">Keine Belege mit diesem Filter.</td></tr>
                ) : sichtbar.map((d) => {
                  const info = statusInfo(d.status);
                  return (
                    <tr key={d.id} data-problem={d.status === "problem"} data-zaehlt={info.zaehlt}
                      onClick={() => setEditing(d)} className="v-klickbar">
                      <td>
                        <div className="v-zelle-titel">{d.vendor_name || d.file_name}</div>
                        {d.mail_subject && <div className="v-zelle-unter">{d.mail_subject}</div>}
                      </td>
                      <td className="v-nowrap">{datumDe(d.issue_date)}</td>
                      <td className="v-schmal-weg mono">{d.invoice_number ?? "—"}</td>
                      <td className="v-num">{euro(d.gross)}</td>
                      <td className="v-num v-schmal-weg">
                        {d.tax_treatment && d.tax_treatment !== "regulaer" ? TAX_KURZ[d.tax_treatment] ?? d.tax_treatment : euro(d.vat_amount)}
                      </td>
                      <td>
                        <span className="v-status" data-status={d.status} title={info.hint}>{info.label}</span>
                        <span className="v-zaehlt" data-zaehlt={info.zaehlt}>{info.zaehlt ? "zählt" : "zählt nicht"}</span>
                      </td>
                      <td className="v-aktion">
                        <button className="btn quiet" onClick={(e) => { e.stopPropagation(); setEditing(d); }}
                          aria-label={`Beleg ${d.vendor_name || d.file_name} ansehen`}>
                          Ansehen
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {zieht && <div className="v-ablegen" aria-hidden="true">PDF hier ablegen</div>}

      {editing && (
        <DocumentEditor
          doc={editing}
          onClose={() => setEditing(null)}
          onSaved={(text) => { setEditing(null); toast.erfolg(text); void load(); onGeaendert(); }}
        />
      )}
    </div>
  );
}

/** Datei als Base64 — über FileReader, damit auch große PDFs nicht den Stack sprengen. */
function alsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(new Error("Datei ließ sich nicht lesen."));
    r.onload = () => {
      const url = String(r.result);
      resolve(url.slice(url.indexOf(",") + 1));
    };
    r.readAsDataURL(file);
  });
}

// ─── Detailansicht ───────────────────────────────────────────────────────────

function DocumentEditor({ doc, onClose, onSaved }: { doc: DocumentRow; onClose: () => void; onSaved: (meldung: string) => void }) {
  const box = useRef<HTMLDivElement | null>(null);
  const [form, setForm] = useState({
    vendorName: doc.vendor_name ?? "",
    invoiceNumber: doc.invoice_number ?? "",
    issueDate: doc.issue_date?.slice(0, 10) ?? "",
    period: doc.period ?? "",
    net: zahlFeld(doc.net, 2),
    vatRate: zahlFeld(doc.vat_rate),
    vatAmount: zahlFeld(doc.vat_amount, 2),
    gross: zahlFeld(doc.gross, 2),
    taxTreatment: doc.tax_treatment ?? "regulaer",
  });
  // Wer speichert, bestätigt den Beleg — außer er ordnet ihn bewusst anders ein.
  const [status, setStatus] = useState<string>(["neu", "problem"].includes(doc.status) ? "geprueft" : doc.status);
  const [ansicht, setAnsicht] = useState<"datei" | "mail">("datei");
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  useDialog(box, onClose, !busy);

  const problems = useMemo(() => {
    try { return JSON.parse(doc.problems || "[]") as string[]; } catch { return []; }
  }, [doc.problems]);
  const istXml = doc.file_name.toLowerCase().endsWith(".xml");
  const dateiUrl = `/api/documents/${doc.id}/file`;
  const zaehlt = statusInfo(status).zaehlt;

  const save = async () => {
    const zahlen = { net: zahlLesen(form.net), vatRate: zahlLesen(form.vatRate), vatAmount: zahlLesen(form.vatAmount), gross: zahlLesen(form.gross) };
    const kaputt = Object.entries(zahlen).filter(([, v]) => v !== null && Number.isNaN(v)).map(([k]) => (
      { net: "Netto", vatRate: "Steuersatz", vatAmount: "Steuerbetrag", gross: "Brutto" } as Record<string, string>)[k]);
    if (kaputt.length) { setFehler(`Bitte eine Zahl eintragen: ${kaputt.join(", ")}.`); return; }
    if (!/^\d{4}-\d{2}$/.test(form.period)) { setFehler("Der Monat braucht die Form JJJJ-MM, etwa 2026-09."); return; }
    setBusy(true);
    setFehler(null);
    try {
      await api.updateDocument(doc.id, {
        vendorName: form.vendorName.trim(),
        invoiceNumber: form.invoiceNumber.trim() || null,
        issueDate: form.issueDate || null,
        period: form.period,
        ...zahlen,
        taxTreatment: form.taxTreatment,
        status,
      });
      const verschoben = form.period !== doc.period ? ` und nach ${periodLabel(form.period)} verschoben` : "";
      onSaved(`Beleg ${form.vendorName || doc.file_name} gespeichert${verschoben}.`);
    } catch (e) {
      setFehler(fehlerText(e));
    } finally {
      setBusy(false);
    }
  };

  /** Regel für den Absender dieses Belegs — ordnet auch alle bisherigen von dort um. */
  const schnellregel = async (aktion: "unterlage" | "ignorieren") => {
    if (!doc.mail_from) return;
    setBusy(true);
    try {
      const r = await api.belegRegelAnlegen("absender", doc.mail_from, aktion);
      onSaved(`Regel „${AKTION_LABEL[aktion].label}" für ${doc.mail_from} angelegt — ${r.angewendet} ${r.angewendet === 1 ? "Beleg" : "Belege"} umsortiert.`);
    } catch (e) {
      setFehler(fehlerText(e));
      setBusy(false);
    }
  };

  const loeschen = async () => {
    const ja = await bestaetigen({
      titel: "Beleg löschen?",
      text: <>„{doc.vendor_name || doc.file_name}" wird samt Datei entfernt. Das lässt sich nicht rückgängig machen.</>,
      bestaetigen: "Endgültig löschen",
      gefahr: true,
    });
    if (!ja) return;
    setBusy(true);
    try {
      await api.deleteDocument(doc.id);
      onSaved(`Beleg ${doc.vendor_name || doc.file_name} gelöscht.`);
    } catch (e) {
      setFehler(fehlerText(e));
      setBusy(false);
    }
  };

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  // Das Datum gibt den Monat vor, solange niemand ihn bewusst anders setzt.
  const setDatum = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, issueDate: v, period: f.period === (f.issueDate.slice(0, 7) || f.period) && v ? v.slice(0, 7) : f.period }));
  };

  return (
    <div className="sheet-backdrop" onClick={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div ref={box} className="sheet beleg-sheet" role="dialog" aria-modal="true" aria-labelledby="beleg-titel">
        <div className="sheet-head">
          <div className="v-sheet-titel">
            <span id="beleg-titel">{doc.vendor_name || doc.file_name}</span>
            <small>{doc.file_name} · {formatBytes(doc.file_size)} · {SOURCE_LABEL[doc.source] ?? doc.source}</small>
          </div>
          <nav className="tabs" aria-label="Vorschau">
            <button className="tab" aria-current={ansicht === "datei"} onClick={() => setAnsicht("datei")}>
              {istXml ? "E-Rechnung (XML)" : "PDF"}
            </button>
            <button className="tab" aria-current={ansicht === "mail"} onClick={() => setAnsicht("mail")} disabled={!doc.mail_id}
              title={doc.mail_id ? "Die Mail, mit der der Beleg kam" : "Keine Herkunftsmail gefunden"}>Herkunftsmail</button>
          </nav>
          <div className="spacer" />
          <a className="btn quiet" href={dateiUrl} target="_blank" rel="noreferrer">
            <ExternalLink aria-hidden="true" /> <span className="v-schmal-weg">In neuem Tab</span>
          </a>
        </div>

        <div className="beleg-split">
          <div className="beleg-vorschau">
            {ansicht === "datei"
              ? <iframe className="beleg-pdf" data-xml={istXml} src={istXml ? dateiUrl : `${dateiUrl}#view=FitH`} title={`Vorschau ${doc.file_name}`} />
              : doc.mail_id ? <MailVorschau id={doc.mail_id} /> : null}
          </div>

          <div className="sheet-body">
            {problems.length > 0 && (
              <div className="notice" data-art="warn" style={{ marginBottom: 16 }}>
                <strong>Beim Auslesen aufgefallen</strong>
                <ul>{problems.map((p, i) => <li key={i}>{p}</li>)}</ul>
              </div>
            )}

            <div className="v-raster">
              <div className="field v-breit"><label htmlFor="b-anbieter">Anbieter</label>
                <input id="b-anbieter" className="input" value={form.vendorName} onChange={set("vendorName")} /></div>
              <div className="field"><label htmlFor="b-nummer">Rechnungsnummer</label>
                <input id="b-nummer" className="input mono" value={form.invoiceNumber} onChange={set("invoiceNumber")} /></div>
              <div className="field"><label htmlFor="b-datum">Rechnungsdatum</label>
                <input id="b-datum" className="input" type="date" value={form.issueDate} onChange={setDatum} /></div>
              <div className="field"><label htmlFor="b-netto">Netto (€)</label>
                <input id="b-netto" className="input v-num" inputMode="decimal" value={form.net} onChange={set("net")} placeholder="0,00" /></div>
              <div className="field"><label htmlFor="b-satz">Steuersatz (%)</label>
                <input id="b-satz" className="input v-num" inputMode="decimal" value={form.vatRate} onChange={set("vatRate")} placeholder="19" /></div>
              <div className="field"><label htmlFor="b-steuer">Steuerbetrag (€)</label>
                <input id="b-steuer" className="input v-num" inputMode="decimal" value={form.vatAmount} onChange={set("vatAmount")} placeholder="0,00" /></div>
              <div className="field"><label htmlFor="b-brutto">Brutto (€)</label>
                <input id="b-brutto" className="input v-num" inputMode="decimal" value={form.gross} onChange={set("gross")} placeholder="0,00" /></div>
            </div>

            <div className="field">
              <label>Steuerliche Behandlung</label>
              <Select
                value={form.taxTreatment}
                onChange={(v) => setForm((f) => ({ ...f, taxTreatment: v }))}
                options={[
                  { value: "regulaer", label: "Deutsche Umsatzsteuer" },
                  { value: "reverse_charge", label: "Reverse-Charge §13b (EU-Ausland)" },
                  { value: "ig_erwerb", label: "Innergemeinschaftlicher Erwerb" },
                  { value: "steuerfrei", label: "Ohne Umsatzsteuer" },
                  { value: "kein_beleg", label: "Kein steuerlicher Beleg" },
                ]}
                aria-label="Steuerliche Behandlung"
              />
            </div>

            <div className="v-raster">
              <div className="field">
                <label>Einordnung</label>
                <Select
                  value={status}
                  onChange={setStatus}
                  options={[
                    { value: "geprueft", label: "Geprüft", hint: "zählt" },
                    { value: "problem", label: "Später prüfen", hint: "zählt nicht" },
                    { value: "dublette", label: "Doppelt", hint: "zählt nicht" },
                    { value: "ausgang", label: "Eigene Ausgangsrechnung", hint: "zählt nicht" },
                    { value: "kein_beleg", label: "Kein Beleg", hint: "zählt nicht" },
                    { value: "unterlage", label: "Unterlage (Auswertung, Lohn …)", hint: "zählt nicht" },
                    { value: "fremd", label: "Nicht an mich adressiert", hint: "zählt nicht" },
                    { value: "ignoriert", label: "Aussortiert", hint: "zählt nicht" },
                    ...(doc.status === "neu" ? [{ value: "neu", label: "Neu (ungeprüft lassen)", hint: "zählt" }] : []),
                  ]}
                  aria-label="Einordnung"
                />
              </div>
              <div className="field">
                <label htmlFor="b-monat">Monat der Voranmeldung</label>
                <input id="b-monat" className="input mono" value={form.period} onChange={set("period")} placeholder="2026-09" />
              </div>
            </div>
            {doc.recipient && <p className="hint" style={{ margin: "0 0 10px" }}>Adressiert an: <b>{doc.recipient}</b></p>}
            <p className="v-zaehlt-hinweis" data-zaehlt={zaehlt}>
              {zaehlt
                ? <>Zählt in die Voranmeldung {form.period && /^\d{4}-\d{2}$/.test(form.period) ? periodLabel(form.period) : ""}.</>
                : <>Zählt nicht in die Voranmeldung.</>}
            </p>

            {doc.mail_from && (
              <div className="beleg-schnellregel">
                <span className="hint" style={{ margin: 0 }}>Für alles von <b>{doc.mail_from}</b>:</span>
                {(["unterlage", "ignorieren"] as const).map((aktion) => (
                  <button key={aktion} className="btn quiet klein" disabled={busy} title={AKTION_LABEL[aktion].hint}
                    onClick={() => void schnellregel(aktion)}>
                    {AKTION_LABEL[aktion].label}
                  </button>
                ))}
              </div>
            )}

            {fehler && <p className="error" role="alert">{fehler}</p>}
          </div>
        </div>

        <div className="sheet-foot">
          <button className="btn danger" onClick={loeschen} disabled={busy}>
            <Trash2 aria-hidden="true" /> Löschen
          </button>
          <div className="spacer" />
          <button className="btn quiet" onClick={onClose} disabled={busy}>Abbrechen</button>
          <button className="btn primary" onClick={save} disabled={busy}>{busy ? "Speichert …" : "Speichern"}</button>
        </div>
      </div>
    </div>
  );
}

/** Die Herkunftsmail eines Belegs, gleich neben dem PDF. */
function MailVorschau({ id }: { id: number }) {
  const [mail, setMail] = useState<MessageDetail | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  useEffect(() => {
    let aktiv = true;
    api.message(id).then((m) => aktiv && setMail(m)).catch((e) => aktiv && setFehler(fehlerText(e)));
    return () => { aktiv = false; };
  }, [id]);
  if (fehler) return <p className="error" style={{ margin: 16 }}>Die Mail ließ sich nicht laden: {fehler}</p>;
  if (!mail) return <div className="v-laden" role="status">Lade E-Mail …</div>;
  return (
    <div className="beleg-mail">
      <div className="reader-subject" style={{ margin: "0 0 8px", fontSize: 18 }}>{mail.subject || "(ohne Betreff)"}</div>
      <div className="reader-from"><b>{mail.from_name || mail.from_email}</b><span>{mail.from_email}</span><span>{formatDate(mail.date)}</span></div>
      <div className="reader-content" style={{ marginTop: 14, paddingTop: 14 }}>
        {mail.body_html ? <MailRahmen html={mail.body_html} bilderFrei={false} /> : <pre>{mail.body_text ?? mail.snippet}</pre>}
      </div>
    </div>
  );
}
